import { PLATFORM_BASE_URL } from "../platform/config";
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import React from "react";
import {
  LuActivity,
  LuBuilding2,
  LuMonitorCheck,
  LuUsers,
} from "react-icons/lu";
import {
  OktonErrorState,
  OktonInfoCard,
  OktonMetricCard,
  OktonLoadingState,
  OktonPageHeader,
  OktonPanel,
  useOktonAuth,
  formatDateTime as formatDate,
} from "@okton/platform-ui";

interface DashboardData {
  metrics: {
    systems: number;
    activeSessions: number;
    users: number;
    companies: number;
    activeBillingBranches: number;
  };
  systems: Array<{
    id: string;
    name: string;
    active: boolean;
    url?: string;
  }>;
  sessionAnalytics: {
    periodDays: number;
    summary: {
      totalSessions: number;
      uniqueUsers: number;
      averageDurationSeconds: number;
      peakConcurrentSessions: number;
      expiredSessions: number;
      adminRevokedSessions: number;
    };
    topCompaniesBySessions: Array<{
      companyId: string;
      companyName?: string;
      sessions: number;
      users: number;
      lastSeenAt?: string;
    }>;
    topCompaniesByUsers: Array<{
      companyId: string;
      companyName?: string;
      sessions: number;
      users: number;
      lastSeenAt?: string;
    }>;
    topSystems: Array<{ clientId: string; sessions: number }>;
    topUsers: Array<{
      subjectId: string;
      userName?: string;
      userEmail?: string;
      sessions: number;
      lastSeenAt?: string;
    }>;
    daily: Array<{ day: string; sessions: number; users: number }>;
  };
  recentEvents: Array<{
    id: string;
    action: string;
    applicationId?: string;
    createdAt: string;
    userName?: string;
    userEmail?: string;
  }>;
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 0) return "0 min";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
}

function csvCell(value: string | number | undefined): string {
  const text = String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
}

export const DashboardPage: React.FC = () => {
  const { accessToken } = useOktonAuth();
  const [data, setData] = React.useState<DashboardData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  React.useEffect(() => {
    let active = true;

    async function load(): Promise<void> {
      if (!accessToken) return;
      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `${PLATFORM_BASE_URL}/v1/admin/dashboard`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: "include",
          },
        );
        const payload = await response.json();
        if (!response.ok)
          throw new Error(
            payload.message || "Não foi possível carregar o dashboard.",
          );
        if (active) setData(payload.data);
      } catch (err) {
        if (active)
          setError(
            err instanceof Error ? err.message : "Erro ao carregar dashboard.",
          );
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [accessToken]);

  if (loading) {
    return <OktonLoadingState title="Carregando dashboard..." minH="50vh" />;
  }

  if (error || !data) {
    return (
      <OktonErrorState
        title="Dashboard indisponível"
        description={
          error || "Não foi possível carregar os dados do dashboard."
        }
        minH="50vh"
      />
    );
  }

  const cards = [
    {
      id: "systems",
      label: "Sistemas Ativos",
      value: data.metrics.systems,
      icon: LuMonitorCheck,
    },
    {
      id: "sessions",
      label: "Sessões Ativas",
      value: data.metrics.activeSessions,
      icon: LuActivity,
    },
    {
      id: "users",
      label: "Usuários Conhecidos",
      value: data.metrics.users,
      icon: LuUsers,
    },
    {
      id: "companies",
      label: "Empresas Ativas",
      value: data.metrics.companies,
      icon: LuBuilding2,
    },
    {
      id: "billingBranches",
      label: "Filiais Ativas com Cobrança",
      value: data.metrics.activeBillingBranches,
      icon: LuBuilding2,
    },
  ];

  const exportMetricsCsv = (): void => {
    const rows: Array<Array<string | number | undefined>> = [
      ["Resumo", "Valor"],
      ["Período (dias)", data.sessionAnalytics.periodDays],
      ["Sessões no período", data.sessionAnalytics.summary.totalSessions],
      ["Usuários únicos", data.sessionAnalytics.summary.uniqueUsers],
      [
        "Duração média (segundos)",
        data.sessionAnalytics.summary.averageDurationSeconds,
      ],
      ["Pico simultâneo", data.sessionAnalytics.summary.peakConcurrentSessions],
      ["Sessões expiradas", data.sessionAnalytics.summary.expiredSessions],
      [
        "Revogações administrativas",
        data.sessionAnalytics.summary.adminRevokedSessions,
      ],
      [],
      ["Empresas com mais acessos"],
      ["Empresa", "Nome", "Sessões", "Usuários únicos"],
      ...data.sessionAnalytics.topCompaniesBySessions.map((item) => [
        item.companyId,
        item.companyName,
        item.sessions,
        item.users,
      ]),
      [],
      ["Sistemas mais usados"],
      ["Sistema", "Sessões"],
      ...data.sessionAnalytics.topSystems.map((item) => [
        item.clientId,
        item.sessions,
      ]),
      [],
      ["Usuários mais ativos"],
      ["Usuário", "E-mail", "Sessões", "Último acesso"],
      ...data.sessionAnalytics.topUsers.map((item) => [
        item.userName,
        item.userEmail,
        item.sessions,
        item.lastSeenAt,
      ]),
      [],
      ["Acessos por dia"],
      ["Data", "Sessões", "Usuários"],
      ...data.sessionAnalytics.daily.map((item) => [
        item.day,
        item.sessions,
        item.users,
      ]),
    ];

    const csv = `\uFEFF${rows.map((row) => row.map((cell) => csvCell(cell)).join(";")).join("\r\n")}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `metricas-sso-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 16]} w="100%">
      <OktonPageHeader
        title="Dashboard"
        actions={
          <Button variant="outline" onClick={exportMetricsCsv}>
            Exportar métricas
          </Button>
        }
      />

      <SimpleGrid columns={[1, 2, 3, 5]} spacing="4" mb="6">
        {cards.map((card) => (
          <OktonMetricCard
            key={card.id}
            label={card.label}
            value={card.value}
            icon={card.icon}
            description={<Badge colorScheme="green">Tempo real</Badge>}
            height="150px"
          />
        ))}
      </SimpleGrid>

      <SimpleGrid columns={[1, 2, 3, 6]} spacing="4" mb="6">
        <OktonInfoCard
          label="Sessões no período"
          value={String(data.sessionAnalytics.summary.totalSessions)}
        />
        <OktonInfoCard
          label="Usuários únicos"
          value={String(data.sessionAnalytics.summary.uniqueUsers)}
        />
        <OktonInfoCard
          label="Duração média"
          value={formatDuration(
            data.sessionAnalytics.summary.averageDurationSeconds,
          )}
        />
        <OktonInfoCard
          label="Pico simultâneo"
          value={String(data.sessionAnalytics.summary.peakConcurrentSessions)}
        />
        <OktonInfoCard
          label="Sessões expiradas"
          value={String(data.sessionAnalytics.summary.expiredSessions)}
        />
        <OktonInfoCard
          label="Revogações administrativas"
          value={String(data.sessionAnalytics.summary.adminRevokedSessions)}
        />
      </SimpleGrid>

      <Grid templateColumns={["1fr", "1fr", "1fr 1fr"]} gap="6" mb="6">
        <GridItem>
          <RankingGrid
            title="Empresas com mais acessos"
            subtitle={`Últimos ${data.sessionAnalytics.periodDays} dias`}
            rows={data.sessionAnalytics.topCompaniesBySessions}
            valueKey="sessions"
          />
        </GridItem>
        <GridItem>
          <RankingGrid
            title="Usuários por empresa"
            subtitle="Ordenado do maior para o menor"
            rows={data.sessionAnalytics.topCompaniesByUsers}
            valueKey="users"
          />
        </GridItem>
      </Grid>

      <Grid templateColumns={["1fr", "1fr", "repeat(3, 1fr)"]} gap="6" mb="6">
        <GridItem>
          <SimpleListGrid
            title="Sistemas mais usados"
            subtitle={`Últimos ${data.sessionAnalytics.periodDays} dias`}
            rows={data.sessionAnalytics.topSystems.map((item) => ({
              id: item.clientId,
              label: item.clientId,
              value: item.sessions,
              detail: "sessões",
            }))}
          />
        </GridItem>
        <GridItem>
          <SimpleListGrid
            title="Usuários mais ativos"
            subtitle="Ordenado por quantidade de sessões"
            rows={data.sessionAnalytics.topUsers.slice(0, 10).map((item) => ({
              id: item.subjectId,
              label: item.userName || item.userEmail || item.subjectId,
              value: item.sessions,
              detail: item.userEmail || "sessões",
            }))}
          />
        </GridItem>
        <GridItem>
          <SimpleListGrid
            title="Acessos por dia"
            subtitle="Últimos registros do período"
            rows={data.sessionAnalytics.daily
              .slice(-14)
              .reverse()
              .map((item) => ({
                id: item.day,
                label: new Intl.DateTimeFormat("pt-BR").format(
                  new Date(`${item.day}T12:00:00`),
                ),
                value: item.sessions,
                detail: `${item.users} usuários`,
              }))}
          />
        </GridItem>
      </Grid>

      <Grid templateColumns={["1fr", "1fr", "1fr 1fr"]} gap="6">
        <GridItem>
          <OktonPanel
            title="Atividade recente"
            subtitle="Últimos eventos registrados pela plataforma"
            bodyStyle={{ padding: 0 }}
          >
            <Stack spacing="0">
              {data.recentEvents.length === 0 ? (
                <Text p="5" color="gray.500">
                  Nenhuma atividade registrada.
                </Text>
              ) : (
                data.recentEvents.map((event) => (
                  <Box
                    key={event.id}
                    px="5"
                    py="3"
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                  >
                    <Flex justify="space-between" gap="3">
                      <Box minW="0">
                        <Text fontSize="sm" fontWeight="600">
                          {event.action}
                        </Text>
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                          {event.userName || event.userEmail || "Usuário"} •{" "}
                          {event.applicationId || "-"}
                        </Text>
                      </Box>
                      <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                        {formatDate(event.createdAt)}
                      </Text>
                    </Flex>
                  </Box>
                ))
              )}
            </Stack>
          </OktonPanel>
        </GridItem>

        <GridItem>
          <OktonPanel
            title="Status dos Sistemas"
            subtitle="Aplicações registradas no Platform Core"
            bodyStyle={{ padding: 0 }}
          >
            <Stack spacing="0">
              {data.systems.map((system) => (
                <Flex
                  key={system.id}
                  px="5"
                  py="4"
                  justify="space-between"
                  align="center"
                  borderBottomWidth="1px"
                  borderColor={borderColor}
                >
                  <Box>
                    <Text fontWeight="600" fontSize="sm">
                      {system.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {system.id}
                    </Text>
                  </Box>
                  <Badge colorScheme={system.active ? "green" : "gray"}>
                    {system.active ? "Ativo" : "Inativo"}
                  </Badge>
                </Flex>
              ))}
            </Stack>
          </OktonPanel>
        </GridItem>
      </Grid>
    </Box>
  );
};

const RankingGrid: React.FC<{
  title: string;
  subtitle: string;
  rows: Array<{
    companyId: string;
    companyName?: string;
    sessions: number;
    users: number;
  }>;
  valueKey: "sessions" | "users";
}> = ({ title, subtitle, rows, valueKey }) => {
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <OktonPanel title={title} subtitle={subtitle} bodyStyle={{ padding: 0 }}>
      <Box>
        {rows.slice(0, 15).map((item, index) => (
          <Grid
            key={item.companyId}
            templateColumns="42px 1fr 90px"
            gap="3"
            px="5"
            py="3"
            borderBottomWidth="1px"
            borderColor={borderColor}
            alignItems="center"
          >
            <Text fontWeight="700">#{index + 1}</Text>
            <Box minW="0">
              <Text fontWeight="600" noOfLines={1}>
                {item.companyId} - {item.companyName || "Empresa"}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {valueKey === "sessions"
                  ? `${item.users} usuários únicos`
                  : `${item.sessions} sessões`}
              </Text>
            </Box>
            <Text textAlign="right" fontWeight="700">
              {item[valueKey]}
            </Text>
          </Grid>
        ))}
      </Box>
    </OktonPanel>
  );
};

const SimpleListGrid: React.FC<{
  title: string;
  subtitle: string;
  rows: Array<{ id: string; label: string; value: number; detail: string }>;
}> = ({ title, subtitle, rows }) => {
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <OktonPanel title={title} subtitle={subtitle} bodyStyle={{ padding: 0 }}>
      <Box>
        {rows.length === 0 ? (
          <Text p="5" color="gray.500">
            Nenhum dado no período.
          </Text>
        ) : (
          rows.map((item, index) => (
            <Grid
              key={item.id}
              templateColumns="36px 1fr 80px"
              gap="3"
              px="5"
              py="3"
              borderBottomWidth="1px"
              borderColor={borderColor}
              alignItems="center"
            >
              <Text fontSize="sm" fontWeight="700">
                #{index + 1}
              </Text>
              <Box minW="0">
                <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                  {item.label}
                </Text>
                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                  {item.detail}
                </Text>
              </Box>
              <Text textAlign="right" fontWeight="700">
                {item.value}
              </Text>
            </Grid>
          ))
        )}
      </Box>
    </OktonPanel>
  );
};
